import React, { useState, useRef } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Alert,
  Chip,
  InputAdornment
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useNavigate } from 'react-router-dom';
import { BarcodeInput } from '../components/BarcodeInput';
import { getProductByBarcode, getGroupedProductByName } from '../services/productService';
import {
  getStockBalance,
  addStockIn,
  addStockOut,
  isBarcodeUsedForStockIn,
  getStockOutInfoForBarcode
} from '../services/stockService';
import { Product, StockBalance } from '../types';
import { useAuth } from '../context/AuthContext';

const formatNumber = (num: number): string =>
  num.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');

const BTN_COLOR = '#78121c';
const BTN_HOVER = '#5c0e15';

const REASON_CONFIG = {
  invoice: { label: 'Invoice Number', placeholder: 'Enter invoice number', notePrefix: 'Invoice #' },
  warranty: { label: 'Warranty Reference Number', placeholder: 'Enter warranty reference number', notePrefix: 'Warranty Ref #' },
  damage: { label: 'Damage Reference Number', placeholder: 'Enter damage reference number', notePrefix: 'Damage Ref #' },
  mrn: { label: 'MRN Number', placeholder: 'Enter MRN number', notePrefix: 'MRN #' },
  dn: { label: 'DN Number', placeholder: 'Enter DN number', notePrefix: 'DN #' }
} as const;

const REASON_LABELS: Record<string, string> = {
  invoice: 'Invoice',
  warranty: 'Warranty',
  damage: 'Damage',
  mrn: 'MRN',
  dn: 'DN'
};

type ScanFeedback = 'none' | 'invalid' | 'duplicate_in' | 'duplicate_out' | 'duplicate_both' | 'success';

export const ScanBarcode: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [product, setProduct] = useState<Product | null>(null);
  const [stockBalance, setStockBalance] = useState<StockBalance | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [feedback, setFeedback] = useState<ScanFeedback>('none');
  const [stockInOpen, setStockInOpen] = useState(false);
  const [stockOutOpen, setStockOutOpen] = useState(false);
  const [quantity, setQuantity] = useState('1');
  const [stockOutQuantity, setStockOutQuantity] = useState('1');
  const [stockOutBarcodes, setStockOutBarcodes] = useState<string[]>([]);
  const [stockOutCurrentBarcode, setStockOutCurrentBarcode] = useState('');
  const [date, setDate] = useState<Date | null>(new Date());
  const [notes, setNotes] = useState('');
  const [stockOutReason, setStockOutReason] = useState<'invoice' | 'warranty' | 'damage' | 'mrn' | 'dn'>('invoice');
  const [referenceNumber, setReferenceNumber] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [usedForIn, setUsedForIn] = useState(false);
  const [usedForOut, setUsedForOut] = useState(false);
  const [soldDate, setSoldDate] = useState<Date | null>(null);
  const [saleInvoiceNumber, setSaleInvoiceNumber] = useState<string | null>(null);
  const [saleInvoiceDate, setSaleInvoiceDate] = useState<Date | null>(null);
  const [saleWarrantyNumber, setSaleWarrantyNumber] = useState<string | null>(null);
  const [productNameInput, setProductNameInput] = useState('');
  const [lookedUpByName, setLookedUpByName] = useState(false);
  const stockOutInputRef = useRef<HTMLInputElement>(null);

  const handleScan = async (barcode: string) => {
    setLoading(true);
    setError('');
    setFeedback('none');
    setProduct(null);
    setStockBalance(null);
    setUsedForIn(false);
    setUsedForOut(false);
    setSoldDate(null);
    setSaleInvoiceNumber(null);
    setSaleInvoiceDate(null);
    setSaleWarrantyNumber(null);
    setLookedUpByName(false);

    try {
      const productData = await getProductByBarcode(barcode);
      if (!productData) {
        setError(`Product with barcode "${barcode}" not found.`);
        setFeedback('invalid');
        return;
      }

      const [balance, inUsed, stockOutInfo] = await Promise.all([
        getStockBalance(barcode),
        isBarcodeUsedForStockIn(barcode),
        getStockOutInfoForBarcode(barcode)
      ]);

      setProduct(productData);
      setStockBalance(balance);
      setUsedForIn(inUsed);
      setUsedForOut(stockOutInfo.usedForOut);
      setSoldDate(stockOutInfo.soldDate);
      setSaleInvoiceNumber(stockOutInfo.invoiceNumber);
      setSaleInvoiceDate(stockOutInfo.invoiceDate);
      setSaleWarrantyNumber(stockOutInfo.warrantyNumber);

      if (inUsed && stockOutInfo.usedForOut) {
        setFeedback('duplicate_both');
      } else if (inUsed) {
        setFeedback('duplicate_in');
      } else if (stockOutInfo.usedForOut) {
        setFeedback('duplicate_out');
      } else {
        setFeedback('success');
      }
    } catch (err: unknown) {
      setError((err as Error).message || 'Error fetching product');
      setFeedback('invalid');
    } finally {
      setLoading(false);
    }
  };

  const handleLookupByName = async () => {
    const name = productNameInput.trim();
    if (!name) return;
    setLoading(true);
    setError('');
    setFeedback('none');
    setProduct(null);
    setStockBalance(null);
    setUsedForIn(false);
    setUsedForOut(false);
    setSoldDate(null);
    setSaleInvoiceNumber(null);
    setSaleInvoiceDate(null);
    setSaleWarrantyNumber(null);
    setLookedUpByName(false);

    try {
      const group = await getGroupedProductByName(name);
      if (!group) {
        setError(`No product found with name "${name}".`);
        setFeedback('invalid');
        return;
      }
      setProduct(group.representative);
      setStockBalance({
        barcode: group.representative.barcode,
        totalIn: group.totalStockIn,
        totalOut: group.totalStockOut,
        balance: group.balance
      });
      setUsedForIn(false);
      setUsedForOut(false);
      setSoldDate(null);
      setSaleInvoiceNumber(null);
      setSaleInvoiceDate(null);
      setSaleWarrantyNumber(null);
      setLookedUpByName(true);
      setFeedback('success');
    } catch (err: unknown) {
      setError((err as Error).message || 'Error looking up product');
      setFeedback('invalid');
    } finally {
      setLoading(false);
    }
  };

  const handleStockIn = async () => {
    if (!product || !user) return;

    const qty = parseInt(quantity, 10);
    if (isNaN(qty) || qty <= 0) {
      setError('Please enter a valid quantity (minimum 1)');
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      await addStockIn(product.barcode, qty, user.uid, user.displayName ?? 'User', notes, date ?? undefined);
      setStockInOpen(false);
      setQuantity('1');
      setNotes('');
      setDate(new Date());
      const [balance, inUsed, outInfo] = await Promise.all([
        getStockBalance(product.barcode),
        isBarcodeUsedForStockIn(product.barcode),
        getStockOutInfoForBarcode(product.barcode)
      ]);
      setStockBalance(balance);
      setUsedForIn(inUsed);
      setUsedForOut(outInfo.usedForOut);
      setFeedback(inUsed && outInfo.usedForOut ? 'duplicate_both' : inUsed ? 'duplicate_in' : 'success');
    } catch (err: unknown) {
      setError((err as Error).message || 'Error adding stock');
    } finally {
      setSubmitting(false);
    }
  };

  const handleStockOutQtyChange = (value: string) => {
    setStockOutQuantity(value);
    const qty = parseInt(value, 10);
    if (!isNaN(qty) && qty > 0 && qty <= 50) {
      setStockOutBarcodes(prev => prev.length > qty ? prev.slice(0, qty) : prev);
    }
  };

  const handleStockOutBarcodeScan = () => {
    const barcode = stockOutCurrentBarcode.trim();
    if (!barcode) return;
    const qty = parseInt(stockOutQuantity, 10);
    if (stockOutBarcodes.length >= qty) {
      setError(`All ${qty} barcodes have already been scanned.`);
      return;
    }
    if (stockOutBarcodes.includes(barcode)) {
      setError(`Barcode "${barcode}" already scanned. Each barcode must be unique.`);
      return;
    }
    setStockOutBarcodes(prev => [...prev, barcode]);
    setStockOutCurrentBarcode('');
    setError('');
    setTimeout(() => stockOutInputRef.current?.focus(), 50);
  };

  const handleRemoveStockOutBarcode = (index: number) => {
    setStockOutBarcodes(prev => prev.filter((_, i) => i !== index));
  };

  const handleStockOut = async () => {
    if (!user) return;

    const qty = parseInt(stockOutQuantity, 10);
    if (isNaN(qty) || qty <= 0) {
      setError('Please enter a valid quantity (minimum 1)');
      return;
    }

    const barcodes = stockOutBarcodes.map(b => b.trim());
    if (barcodes.length !== qty) {
      setError(`Please scan all ${qty} barcodes. Currently scanned: ${barcodes.length}`);
      return;
    }

    const config = REASON_CONFIG[stockOutReason];
    if (!referenceNumber.trim()) {
      setError(`Please enter the ${config.label}`);
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      const reasonLabel = REASON_LABELS[stockOutReason];
      const notesToSave = `${config.notePrefix}: ${referenceNumber.trim()}`;

      let successCount = 0;
      for (let i = 0; i < barcodes.length; i++) {
        try {
          await addStockOut(barcodes[i], 1, user.uid, user.displayName ?? 'User', reasonLabel, notesToSave, undefined);
          successCount++;
        } catch (err: unknown) {
          const msg = (err as Error).message || 'Unknown error';
          if (successCount > 0) {
            setError(`Processed ${successCount} of ${barcodes.length} items. Error on item #${i + 1} (${barcodes[i]}): ${msg}`);
          } else {
            setError(`Error on item #${i + 1} (${barcodes[i]}): ${msg}`);
          }
          return;
        }
      }

      setStockOutOpen(false);
      setStockOutQuantity('1');
      setStockOutBarcodes([]);
      setStockOutCurrentBarcode('');
      setStockOutReason('invoice');
      setReferenceNumber('');

      if (product) {
        const [balance, inUsed, outInfo] = await Promise.all([
          getStockBalance(product.barcode),
          isBarcodeUsedForStockIn(product.barcode),
          getStockOutInfoForBarcode(product.barcode)
        ]);
        setStockBalance(balance);
        setUsedForIn(inUsed);
        setUsedForOut(outInfo.usedForOut);
        setSoldDate(outInfo.soldDate);
        setSaleInvoiceNumber(outInfo.invoiceNumber);
        setSaleInvoiceDate(outInfo.invoiceDate);
        setSaleWarrantyNumber(outInfo.warrantyNumber);
        setFeedback(inUsed && outInfo.usedForOut ? 'duplicate_both' : outInfo.usedForOut ? 'duplicate_out' : 'success');
      }
    } catch (err: unknown) {
      setError((err as Error).message || 'Error recording stock out');
    } finally {
      setSubmitting(false);
    }
  };

  const isStoreManager = user?.role === 'store_manager';
  const isLowStock = product && stockBalance && stockBalance.balance <= product.lowStockThreshold;

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h4" component="h1" gutterBottom>
            Scan Barcode
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Each barcode = 1 physical unit. Each barcode can only be used once for Stock IN and once for Stock OUT.
          </Typography>
          <Button onClick={() => navigate('/dashboard')} sx={{ mb: 2, color: BTN_COLOR }}>
            ← Back to Dashboard
          </Button>
        </Box>

        <Paper sx={{ p: 3, mb: 3 }}>
          <BarcodeInput onScan={handleScan} />
          {isStoreManager && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Or search by product name
              </Typography>
              <TextField
                fullWidth
                size="medium"
                placeholder="Enter product name, then press Enter"
                value={productNameInput}
                onChange={(e) => setProductNameInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleLookupByName();
                  }
                }}
                InputProps={{
                  sx: {
                    '& .MuiOutlinedInput-input': { fontSize: '16px' },
                    '& fieldset': { borderColor: BTN_COLOR },
                    '&:hover fieldset': { borderColor: BTN_COLOR, borderWidth: '2px' },
                    '&.Mui-focused fieldset': { borderColor: BTN_COLOR, borderWidth: '2px' }
                  },
                  endAdornment: (
                    <InputAdornment position="end">
                      <Button size="small" onClick={handleLookupByName} sx={{ color: BTN_COLOR }}>
                        Search
                      </Button>
                    </InputAdornment>
                  )
                }}
                sx={{ maxWidth: 480 }}
              />
            </Box>
          )}
          <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 2 }}>
            <Button
              variant="contained"
              onClick={() => setStockOutOpen(true)}
              size="large"
              sx={{ bgcolor: BTN_COLOR, '&:hover': { bgcolor: BTN_HOVER }, fontWeight: 600 }}
            >
              Stock Out
            </Button>
            <Typography variant="body2" color="text.secondary">
              Record outgoing stock with barcode scanning
            </Typography>
          </Box>
        </Paper>

        {/* Visual feedback: invalid barcode */}
        {feedback === 'invalid' && (
          <Alert severity="error" sx={{ mb: 2 }} icon={false}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography color="error.main" fontWeight="bold">Invalid barcode</Typography>
              <Chip label="Not found" color="error" size="small" />
            </Box>
            <Typography variant="body2">{error}</Typography>
          </Alert>
        )}

        {/* Visual feedback: product found */}
        {product && feedback !== 'invalid' && (
          <Alert severity="success" sx={{ mb: 2 }} icon={false}>
            <Typography fontWeight="bold">🟢 Product Found</Typography>
          </Alert>
        )}

        {(feedback === 'duplicate_out' || feedback === 'duplicate_both') && product && (
          <Alert severity="warning" sx={{ mb: 2 }} icon={false}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Typography color="warning.dark" fontWeight="bold">
                {feedback === 'duplicate_both'
                  ? 'Duplicate scan – barcode fully used (IN and OUT)'
                  : 'Duplicate scan – already used for Stock OUT'}
              </Typography>
              <Chip
                label={feedback === 'duplicate_both' ? 'Barcode lifecycle complete' : 'Barcode already stocked out'}
                color="warning"
                size="small"
              />
            </Box>
            <Typography variant="body2">
              {feedback === 'duplicate_both'
                ? 'This barcode cannot be used again for Stock IN or Stock OUT.'
                : 'This barcode was already used for Stock OUT.'}
            </Typography>
          </Alert>
        )}

        {error && feedback !== 'invalid' && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {loading && (
          <Paper sx={{ p: 3 }}>
            <Typography>{productNameInput.trim() ? 'Looking up product by name...' : 'Validating barcode and fetching product...'}</Typography>
          </Paper>
        )}

        {product && stockBalance && (
          <Grid container spacing={3} alignItems="stretch">
            <Grid item xs={12} md={6} sx={{ display: 'flex' }}>
              <Card
                sx={{
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'background-color 0.25s ease, box-shadow 0.25s ease, transform 0.25s ease',
                  '&:hover': {
                    bgcolor: 'rgba(120, 18, 28, 0.06)',
                    boxShadow: '0 8px 24px rgba(120, 18, 28, 0.12)',
                    transform: 'translateY(-2px)'
                  }
                }}
              >
                <CardContent sx={{ flex: 1, '& .MuiTypography-root': { fontSize: '1.25rem' } }}>
                  <Typography variant="h6" gutterBottom sx={{ fontSize: '1.5rem', fontWeight: 700 }}>
                    Product Details
                  </Typography>
                  <Typography><strong>Name:</strong> {product.name}</Typography>
                  {!lookedUpByName && <Typography><strong>Barcode:</strong> {product.barcode}</Typography>}
                  {lookedUpByName && (
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '1.125rem' }}>
                      (Aggregated across all barcodes for this product)
                    </Typography>
                  )}
                  <Typography><strong>Category:</strong> {product.categoryCode || product.category}</Typography>
                  <Typography><strong>Purchase Price:</strong> Rs. {formatNumber(product.costPrice)}</Typography>
                  <Typography><strong>Selling Price:</strong> Rs. {formatNumber(product.sellingPrice ?? 0)}</Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6} sx={{ display: 'flex' }}>
              <Card
                sx={{
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  border: isLowStock && !usedForOut && !lookedUpByName ? 2 : 0,
                  borderColor: 'warning.main',
                  transition: 'background-color 0.25s ease, box-shadow 0.25s ease, transform 0.25s ease',
                  '&:hover': {
                    bgcolor: 'rgba(0, 137, 123, 0.08)',
                    boxShadow: '0 8px 24px rgba(0, 137, 123, 0.15)',
                    transform: 'translateY(-2px)'
                  }
                }}
              >
                <CardContent sx={{ flex: 1, p: 3, '&:last-child': { pb: 3 } }}>
                  <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                    Stock Information
                  </Typography>
                  {lookedUpByName ? (
                    <>
                      <Typography variant="h3" component="p" sx={{ color: isLowStock ? 'warning.main' : 'success.main', fontWeight: 700, py: 2, px: 0 }}>
                        Available: {stockBalance.balance}
                      </Typography>
                      {isLowStock && (
                        <Alert severity="warning" sx={{ mt: 2 }}>
                          Low stock! Balance ({stockBalance.balance}) is at or below threshold ({product.lowStockThreshold})
                        </Alert>
                      )}
                    </>
                  ) : usedForOut ? (
                    <Box sx={{ py: 2, px: 0 }}>
                      <Typography sx={{ fontWeight: 600, color: 'text.primary', fontSize: '1.25rem' }}>
                        Status: SOLD
                      </Typography>
                      <Typography sx={{ mt: 1, color: 'text.secondary', fontSize: '1.125rem' }}>
                        Sold Date: {soldDate ? soldDate.toLocaleDateString(undefined, { dateStyle: 'medium' }) : '–'}
                      </Typography>
                      {soldDate && (
                        <Typography sx={{ mt: 1, color: 'text.secondary', fontSize: '1.125rem' }}>
                          Sold Time: {soldDate.toLocaleTimeString(undefined, { timeStyle: 'short' })}
                        </Typography>
                      )}
                      {saleInvoiceNumber && (
                        <Typography sx={{ mt: 1, color: 'text.secondary', fontSize: '1.125rem' }}>
                          Invoice No: {saleInvoiceNumber}
                        </Typography>
                      )}
                      {saleInvoiceDate && (
                        <Typography sx={{ mt: 1, color: 'text.secondary', fontSize: '1.125rem' }}>
                          Invoice Date: {saleInvoiceDate.toLocaleDateString(undefined, { dateStyle: 'medium' })}
                        </Typography>
                      )}
                      {saleWarrantyNumber && (
                        <Typography sx={{ mt: 1, color: 'text.secondary', fontSize: '1.125rem' }}>
                          Warranty No: {saleWarrantyNumber}
                        </Typography>
                      )}
                    </Box>
                  ) : (
                    <>
                      <Typography
                        variant="h3"
                        component="p"
                        sx={{
                          color: isLowStock ? 'warning.main' : 'success.main',
                          fontWeight: 700,
                          py: 2,
                          px: 0
                        }}
                      >
                        Available
                      </Typography>
                      {isLowStock && (
                        <Alert severity="warning" sx={{ mt: 2 }}>
                          Low stock! Balance ({stockBalance.balance}) is at or below threshold ({product.lowStockThreshold})
                        </Alert>
                      )}
                    </>
                  )}
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                <Button
                  variant="contained"
                  onClick={() => setStockInOpen(true)}
                  size="large"
                  disabled={usedForIn || lookedUpByName}
                  sx={{ bgcolor: BTN_COLOR, '&:hover': { bgcolor: BTN_HOVER } }}
                >
                  Stock IN
                </Button>
                {lookedUpByName && (
                  <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                    Scan a barcode to record Stock In.
                  </Typography>
                )}
                {!lookedUpByName && usedForIn && (
                  <Chip label="This barcode already used for Stock IN" color="warning" size="small" sx={{ ml: 1 }} />
                )}
                {!lookedUpByName && usedForOut && (
                  <Chip label="This barcode already used for Stock OUT" color="warning" size="small" sx={{ ml: 1 }} />
                )}
              </Box>
            </Grid>
          </Grid>
        )}

        <Dialog open={stockInOpen} onClose={() => setStockInOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Add Stock IN (1 unit per barcode scan)</DialogTitle>
          <DialogContent>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
                {error}
              </Alert>
            )}
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Scan barcode to confirm product or switch to another
              </Typography>
              <BarcodeInput
                onScan={handleScan}
                label="Barcode"
                placeholder="Scan barcode here"
              />
            </Box>
            <TextField
              fullWidth
              label="Quantity"
              type="number"
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
              margin="normal"
              required
              inputProps={{ min: 1 }}
              helperText="Each barcode = 1 unit. Enter quantity for bulk entry."
            />
            <DatePicker
              label="Date"
              value={date}
              onChange={(newValue) => setDate(newValue)}
              slotProps={{ textField: { fullWidth: true, margin: 'normal' } }}
            />
            <TextField
              fullWidth
              label="Notes (Optional)"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              margin="normal"
              multiline
              rows={3}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setStockInOpen(false)} sx={{ color: BTN_COLOR }}>Cancel</Button>
            <Button onClick={handleStockIn} variant="contained" disabled={submitting} sx={{ bgcolor: BTN_COLOR, '&:hover': { bgcolor: BTN_HOVER } }}>
              {submitting ? 'Adding...' : 'Add Stock'}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={stockOutOpen}
          onClose={() => {
            setStockOutOpen(false);
            setStockOutQuantity('1');
            setStockOutBarcodes([]);
            setStockOutCurrentBarcode('');
            setStockOutReason('invoice');
            setReferenceNumber('');
            setError('');
          }}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Stock Out</DialogTitle>
          <DialogContent>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
                {error}
              </Alert>
            )}

            <TextField
              fullWidth
              label="Quantity"
              type="number"
              value={stockOutQuantity}
              onChange={(e) => handleStockOutQtyChange(e.target.value)}
              margin="normal"
              required
              inputProps={{ min: 1, max: 50 }}
              helperText="Number of items to stock out"
              autoFocus
            />

            {parseInt(stockOutQuantity, 10) > 0 && (
              <Box sx={{ mt: 1, mb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Barcodes scanned: {stockOutBarcodes.length} / {parseInt(stockOutQuantity, 10)}
                  </Typography>
                  {stockOutBarcodes.length === parseInt(stockOutQuantity, 10) && (
                    <Chip label="All scanned" color="success" size="small" />
                  )}
                </Box>

                {stockOutBarcodes.length < parseInt(stockOutQuantity, 10) && (
                  <TextField
                    fullWidth
                    size="small"
                    label={`Scan barcode ${stockOutBarcodes.length + 1} of ${parseInt(stockOutQuantity, 10)}`}
                    value={stockOutCurrentBarcode}
                    onChange={(e) => setStockOutCurrentBarcode(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleStockOutBarcodeScan();
                      }
                    }}
                    inputRef={stockOutInputRef}
                    margin="dense"
                    placeholder="Scan or type barcode, then press Enter"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <Button size="small" onClick={handleStockOutBarcodeScan} sx={{ color: BTN_COLOR }}>
                            Add
                          </Button>
                        </InputAdornment>
                      )
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': { borderColor: BTN_COLOR },
                        '&:hover fieldset': { borderColor: BTN_COLOR, borderWidth: '2px' },
                        '&.Mui-focused fieldset': { borderColor: BTN_COLOR, borderWidth: '2px' }
                      }
                    }}
                  />
                )}

                {stockOutBarcodes.length > 0 && (
                  <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {stockOutBarcodes.map((barcode, index) => (
                      <Chip
                        key={index}
                        label={`${index + 1}. ${barcode}`}
                        onDelete={() => handleRemoveStockOutBarcode(index)}
                        variant="outlined"
                        size="small"
                        sx={{ fontFamily: 'monospace' }}
                      />
                    ))}
                  </Box>
                )}
              </Box>
            )}

            <TextField
              fullWidth
              select
              label="Reason"
              value={stockOutReason}
              onChange={(e) => {
                setStockOutReason(e.target.value as typeof stockOutReason);
                setReferenceNumber('');
              }}
              margin="normal"
              sx={{ mt: 2 }}
            >
              <MenuItem value="invoice">Invoice</MenuItem>
              <MenuItem value="warranty">Warranty</MenuItem>
              <MenuItem value="damage">Damage</MenuItem>
              <MenuItem value="mrn">MRN</MenuItem>
              <MenuItem value="dn">DN</MenuItem>
            </TextField>

            <TextField
              fullWidth
              label={REASON_CONFIG[stockOutReason].label}
              value={referenceNumber}
              onChange={(e) => setReferenceNumber(e.target.value)}
              margin="normal"
              required
              placeholder={REASON_CONFIG[stockOutReason].placeholder}
            />
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setStockOutOpen(false);
                setStockOutQuantity('1');
                setStockOutBarcodes([]);
                setStockOutCurrentBarcode('');
                setStockOutReason('invoice');
                setReferenceNumber('');
                setError('');
              }}
              sx={{ color: BTN_COLOR }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleStockOut}
              variant="contained"
              disabled={submitting}
              sx={{ bgcolor: BTN_COLOR, '&:hover': { bgcolor: BTN_HOVER } }}
            >
              {submitting ? 'Processing...' : 'Confirm Stock Out'}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </LocalizationProvider>
  );
};
